import { createContext, useCallback, useContext, useEffect, useRef, type FC, type ReactNode } from "react";
import { useLocation } from "react-router";
import type { AmbientContextEnvelope } from "@acto/ambient-context-schema";

/**
 * Global holder for the ambient-context envelope, composed from what is actually
 * rendered. Each rendered component contributes its already-fetched slice of the
 * visible scope (`useContributeChatScope`), tagged with its route. Everything is
 * keyed by the pathname it came from, so the chat adapter assembles the envelope
 * ONLY for the path chat was opened from (`from`) — a stale or mismatched page
 * never leaks. Stored in refs so reads are always current without re-rendering.
 */

/** All routes the envelope supports, and each route's visible_scope — derived from the envelope union so they can't drift. */
type EnvelopeRoute = AmbientContextEnvelope["route"];
type ScopeByRoute = { [E in AmbientContextEnvelope as E["route"]]: E["visible_scope"] };

type VisibleScope = Record<string, unknown>;

interface ChatEnvelopeContextValue {
  /** Merge a fragment into the current path's scope; returns the path it wrote to. */
  contribute: (route: EnvelopeRoute, fragment: VisibleScope) => string;
  /** Remove the given keys from a path's scope — but only while still on that path. */
  withdraw: (path: string, keys: string[]) => void;
  getForPath: (fromPath: string | undefined) => AmbientContextEnvelope | null;
}

const ChatEnvelopeContext = createContext<ChatEnvelopeContextValue | null>(null);

export const ChatEnvelopeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const routeByPath = useRef<Record<string, EnvelopeRoute>>({});
  const scopeByPath = useRef<Record<string, VisibleScope>>({});

  const contribute = useCallback((route: EnvelopeRoute, fragment: VisibleScope) => {
    const path = pathnameRef.current;
    routeByPath.current[path] = route;
    Object.assign((scopeByPath.current[path] ??= {}), fragment);
    return path;
  }, []);

  const withdraw = useCallback((path: string, keys: string[]) => {
    // If the path changed since this fragment was contributed, the component
    // unmounted because we NAVIGATED AWAY — keep the scope so chat opened from
    // that page still sees it. Only clear when still on the same path (e.g. a
    // tab switch), so stale tab data doesn't linger.
    if (pathnameRef.current !== path) return;
    const scope = scopeByPath.current[path];
    if (!scope) return;
    const removed = new Set(keys);
    scopeByPath.current[path] = Object.fromEntries(Object.entries(scope).filter(([key]) => !removed.has(key)));
  }, []);

  const getForPath = useCallback((fromPath: string | undefined): AmbientContextEnvelope | null => {
    if (!fromPath) return null;
    const route = routeByPath.current[fromPath];
    if (!route) return null;
    const visible_scope = scopeByPath.current[fromPath] ?? {};
    // Zod is not applied at this seam — structural correctness is each
    // contributor's responsibility, and the agent validates server-side.
    return { route, visible_scope, recent_interactions: [] } as unknown as AmbientContextEnvelope;
  }, []);

  return (
    <ChatEnvelopeContext.Provider value={{ contribute, withdraw, getForPath }}>{children}</ChatEnvelopeContext.Provider>
  );
};

/** No-op fallback so consumers (and tests / Storybook) work without a provider. */
const NOOP_CHAT_ENVELOPE: ChatEnvelopeContextValue = {
  contribute: () => "",
  withdraw: () => undefined,
  getForPath: () => null,
};

export function useChatEnvelope(): ChatEnvelopeContextValue {
  return useContext(ChatEnvelopeContext) ?? NOOP_CHAT_ENVELOPE;
}

/**
 * A rendered component contributes its slice of the visible scope from data it
 * already has. `route` names which screen this is — it both records the route
 * (keyed by the current path) and types `fragment` against that route's scope,
 * so only that scope's fields are accepted. The contribution is withdrawn when
 * the component unmounts on the same path (e.g. a tab switch), so the scope
 * reflects only what's currently rendered. `fragment` should be memoized by the
 * caller. No-op outside a provider (e.g. Storybook).
 */
export function useContributeChatScope<R extends EnvelopeRoute>(route: R, fragment: Partial<ScopeByRoute[R]>): void {
  const ctx = useContext(ChatEnvelopeContext);
  useEffect(() => {
    if (!ctx) return;
    const path = ctx.contribute(route, fragment as VisibleScope);
    const keys = Object.keys(fragment);
    return () => ctx.withdraw(path, keys);
  }, [ctx, route, fragment]);
}
